import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { geospatialApi } from '../../../api/geospatialApi';
import NlimsDataTable from '../../shared/NlimsDataTable/NlimsDataTable';
import CreateGeospatialModal from '../Modals/CreateGeospatialModal';
import EditGeospatialModal from '../Modals/EditGeospatialModal';
import RecordDetailsModal from '../shared/RecordDetailsModal';
import { useLocationOptions } from '../shared/useLocationOptions';
import SystemRowMenu, { standardRowItems, confirmAction } from '../shared/SystemRowMenu';
import { anchorRect } from '../../shared/useAnchoredMenu';

function humanize(value) {
  if (!value) return '-';
  return String(value).replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
}

function statusBadgeHtml(status, type) {
  if (type !== 'display') return status ?? '';
  if (!status) return '-';

  const colors = {
    draft: 'background:#f3f4f6;color:#6b7280',
    submitted: 'background:#dbeafe;color:#1d4ed8',
    validated: 'background:#fef9c3;color:#92400e',
    approved: 'background:#dcfce7;color:#15803d',
    rejected: 'background:#fee2e2;color:#b91c1c',
  };
  const style = colors[String(status).toLowerCase()] ?? colors.draft;

  return `<span style="display:inline-flex;align-items:center;padding:2px 8px;border-radius:4px;font-size:11px;font-weight:600;${style}">${humanize(status)}</span>`;
}

const ACTION_RENDER = (_data, type) =>
  type !== 'display'
    ? ''
    : `
      <button class="nlims-action-btn" data-action="menu" type="button"
        style="background:#f0fdf4;border:1px solid #bbf7d0;border-radius:8px;padding:3px 12px;cursor:pointer;color:#15803d;font-weight:800;font-size:15px;letter-spacing:2px;line-height:1.4;">&#8943;</button>
    `;

const COLUMNS = [
  { title: 'State', data: 'state', sortable: true },
  { title: 'LGA', data: 'lga', sortable: true, render: (d, t) => (t === 'display' ? (d || '-') : (d ?? '')) },
  { title: 'Ward', data: 'ward', sortable: true, render: (d, t) => (t === 'display' ? (d || '-') : (d ?? '')) },
  { title: 'Ecological Zone', data: 'ecological_zone', sortable: true, render: (d, t) => (t === 'display' ? humanize(d) : (d ?? '')) },
  { title: 'Coordinates', data: 'coordinates', sortable: false, render: (d, t) => (t === 'display' ? (d || '-') : (d ?? '')) },
  { title: 'Boundary', data: 'boundary_polygon', sortable: false, render: (d, t) => (t === 'display' ? (d || '-') : (d ?? '')) },
  { title: 'Status', data: 'data_status', sortable: true, render: statusBadgeHtml },
  { title: 'Action', data: null, sortable: false, render: ACTION_RENDER },
];

function SearchCreateRow({ search, setSearch, onSearch, onCreate, createLabel = 'Create New' }) {
  return (
    <div className="mb-4 flex items-center gap-3">
      <div className="relative flex-1">
        <svg
          className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && onSearch()}
          placeholder="Search here"
          className="w-full rounded-lg border border-gray-200 py-2.5 pl-9 pr-4 text-sm text-gray-700 placeholder-gray-400 focus:border-green-400 focus:outline-none focus:ring-1 focus:ring-green-400"
        />
      </div>
      <button
        type="button"
        onClick={onSearch}
        className="rounded-lg border border-green-200 bg-green-50 px-5 py-2.5 text-sm font-semibold text-green-700 hover:bg-green-100"
      >
        Search
      </button>
      <button
        type="button"
        onClick={onCreate}
        className="flex items-center gap-1.5 rounded-lg bg-green-700 px-5 py-2.5 text-sm font-semibold text-white hover:bg-green-800"
      >
        <span className="text-base font-bold leading-none">+</span> {createLabel}
      </button>
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, disabled = false }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-sm font-medium text-gray-700">{label}</span>
      <div className="relative">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className="cursor-pointer appearance-none rounded-lg border border-gray-200 bg-white py-2 pl-3 pr-8 text-sm text-gray-700 focus:border-green-400 focus:outline-none disabled:cursor-not-allowed disabled:bg-gray-50"
        >
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <svg
          className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2.5}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </div>
    </div>
  );
}

export default function GeospatialReference() {
  const [searchDraft, setSearchDraft] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [stateFilter, setStateFilter] = useState('All');
  const [lgaFilter, setLgaFilter] = useState('All');
  const [wardFilter, setWardFilter] = useState('All');
  const [rows, setRows] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [viewRow, setViewRow] = useState(null);
  const [rowMenu, setRowMenu] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  const stateScope = stateFilter === 'All' ? '' : stateFilter;
  const lgaScope = lgaFilter === 'All' ? '' : lgaFilter;
  const { stateOptions, lgaOptions, wardOptions } = useLocationOptions(stateScope, lgaScope);

  const loadData = useCallback(async () => {
    setError('');
    try {
      const first = await geospatialApi.list({ per_page: 200, page: 1 });
      const lastPage = first.pagination?.last_page ?? 1;
      let allItems = [...first.items];
      if (lastPage > 1) {
        const pages = await Promise.all(
          Array.from({ length: lastPage - 1 }, (_, i) =>
            geospatialApi.list({ per_page: 200, page: i + 2 })
          )
        );
        allItems = [allItems, ...pages.map((p) => p.items)].flat();
      }
      setRows(allItems);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Failed to load geospatial records.');
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const backendFilters = useMemo(() => {
    const params = {};
    if (stateFilter !== 'All') params.state = stateFilter;
    if (lgaFilter !== 'All') params.lga = lgaFilter;
    if (wardFilter !== 'All') params.ward = wardFilter;
    return params;
  }, [lgaFilter, stateFilter, wardFilter]);

  const activeFilters = useMemo(() => {
    const items = [];
    if (stateFilter !== 'All') items.push(`State: ${stateFilter}`);
    if (lgaFilter !== 'All') items.push(`LGA: ${lgaFilter}`);
    if (wardFilter !== 'All') items.push(`Ward: ${wardFilter}`);
    if (searchQuery) items.push(`Search: ${searchQuery}`);
    return items;
  }, [lgaFilter, searchQuery, stateFilter, wardFilter]);

  const handleSearch = () => {
    setSearchQuery(searchDraft.trim());
  };

  const handleStateChange = (value) => {
    setStateFilter(value);
    setLgaFilter('All');
    setWardFilter('All');
  };

  const handleLgaChange = (value) => {
    setLgaFilter(value);
    setWardFilter('All');
  };

  const handleCreate = async () => {
    loadData();
  };

  const handleSave = async () => {
    loadData();
  };

  const handleDelete = async (row) => {
    if (!row?.uuid) return;
    const confirmed = await confirmAction({ title: 'Delete Record?', message: `Delete geospatial record for ${row.state}${row.lga ? ` / ${row.lga}` : ''}?`, confirmLabel: 'Delete', danger: true });
    if (!confirmed) return;

    setDeleting(true);
    try {
      await geospatialApi.remove(row.uuid);
      setViewRow(null);
      loadData();
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        'Delete failed.';
      toast.error(message, { position: 'top-right' });
    } finally {
      setDeleting(false);
    }
  };

  const handleRowAction = (action, row, meta) => {
    if (!row) return;
    if (action === 'menu') { setRowMenu({ row, rect: anchorRect(meta?.button) }); return; }
  };

  return (
    <section>
      <SearchCreateRow
        search={searchDraft}
        setSearch={setSearchDraft}
        onSearch={handleSearch}
        onCreate={() => setCreateOpen(true)}
      />

      <div className="mb-4 flex flex-wrap items-center gap-4">
        <FilterSelect
          label="Filter by State:"
          value={stateFilter}
          onChange={handleStateChange}
          options={[{ value: 'All', label: 'All' }, ...stateOptions]}
        />
        <FilterSelect
          label="Filter by LGA:"
          value={lgaFilter}
          onChange={handleLgaChange}
          options={[{ value: 'All', label: 'All' }, ...lgaOptions]}
          disabled={!stateScope}
        />
        <FilterSelect
          label="Filter by Ward:"
          value={wardFilter}
          onChange={setWardFilter}
          options={[{ value: 'All', label: 'All' }, ...wardOptions]}
          disabled={!stateScope || lgaFilter === 'All'}
        />
      </div>

      {activeFilters.length > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-2 rounded-2xl border border-green-100 bg-green-50 px-4 py-3 text-sm text-green-800">
          <span className="font-semibold">Applied filters:</span>
          {activeFilters.map((item) => (
            <span
              key={item}
              className="rounded-full border border-green-200 bg-white px-3 py-1 text-xs font-medium text-green-700"
            >
              {item}
            </span>
          ))}
        </div>
      )}

      {error && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <span>{error}</span>
          <button
            type="button"
            onClick={loadData}
            className="rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-100"
          >
            Retry
          </button>
        </div>
      )}

      <NlimsDataTable
        tableId="geospatial-table"
        columns={COLUMNS}
        data={rows}
        serverSide={false}
        filters={backendFilters}
        searchQuery={searchQuery}
        hideSearch
        pageLength={10}
        enableRowSelection
        defaultSort={[0, 'asc']}
        onRowAction={handleRowAction}
      />

      {rowMenu && (
        <SystemRowMenu
          rect={rowMenu.rect}
          items={standardRowItems()}
          onClose={() => setRowMenu(null)}
          onPick={(key) => { const row = rowMenu.row; if (key === 'view') setViewRow(row); if (key === 'edit') setEditRow(row); if (key === 'delete') handleDelete(row); }}
        />
      )}

      <RecordDetailsModal
        isOpen={!!viewRow}
        onClose={() => setViewRow(null)}
        onEdit={() => {
          setEditRow(viewRow);
          setViewRow(null);
        }}
        onDelete={() => handleDelete(viewRow)}
        deleting={deleting}
        title="Location Details"
        subtitle={[viewRow?.state, viewRow?.lga, viewRow?.ward].filter(Boolean).join(' / ')}
        statusValue={viewRow?.data_status}
        fields={viewRow ? [
          { label: 'State', value: viewRow.state || '-' },
          { label: 'LGA', value: viewRow.lga || '-' },
          { label: 'Ward', value: viewRow.ward || '-' },
          { label: 'Ecological Zone', value: humanize(viewRow.ecological_zone) },
          { label: 'Coordinates', value: viewRow.coordinates || '-' },
          { label: 'Boundary', value: viewRow.boundary_polygon || '-' },
          { label: 'Status', value: humanize(viewRow.data_status) },
          { label: 'UUID', value: viewRow.uuid, mono: true, wide: true },
        ] : []}
      />

      {createOpen && (
        <CreateGeospatialModal
          isOpen={createOpen}
          onClose={() => setCreateOpen(false)}
          onCreate={handleCreate}
        />
      )}

      {editRow && (
        <EditGeospatialModal
          isOpen={!!editRow}
          onClose={() => setEditRow(null)}
          onSave={handleSave}
          onDelete={handleDelete}
          row={editRow}
        />
      )}
    </section>
  );
}
